/**
 * BOVWriteupPanel — generates a Claude-drafted BOV / OM narrative for
 * the property using everything we know: metadata, location, tenants,
 * pro forma. Output is editable Markdown the broker can copy or refine.
 */

import { useState } from "react";
import { FileText, Sparkles, RefreshCw, Copy, Check } from "lucide-react";
import { generateBOVWriteup, type BOVWriteup } from "../../api/client";
import { useToast } from "../../contexts/ToastContext";
import { friendlyError } from "../../utils/errors";

interface Props {
  dealId: string;
}

export default function BOVWriteupPanel({ dealId }: Props) {
  const toast = useToast();
  const [writeup, setWriteup] = useState<BOVWriteup | null>(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  async function handleGenerate() {
    setLoading(true);
    try {
      const result = await generateBOVWriteup(dealId);
      setWriteup(result);
    } catch (e: any) {
      toast.error(
        friendlyError(
          e?.response?.data?.detail ||
            "Couldn't generate the writeup. Try again in a moment.",
        ),
      );
    } finally {
      setLoading(false);
    }
  }

  async function handleCopy() {
    if (!writeup) return;
    try {
      await navigator.clipboard.writeText(writeup.markdown);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Couldn't access the clipboard.");
    }
  }

  return (
    <div className="bg-white rounded-xl border border-grove-300 shadow-sm p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-lg font-semibold text-bark-800 flex items-center gap-2">
          <FileText className="w-4 h-4 text-forest-700" />
          BOV / OM Writeup
        </h3>
        <div className="flex items-center gap-2">
          {writeup && (
            <button
              onClick={handleCopy}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium border border-grove-300 rounded-lg text-bark-700 hover:bg-grove-50 transition-all"
            >
              {copied ? (
                <Check className="w-3.5 h-3.5 text-forest-600" />
              ) : (
                <Copy className="w-3.5 h-3.5" />
              )}
              {copied ? "Copied" : "Copy"}
            </button>
          )}
          <button
            onClick={handleGenerate}
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold bg-honey-600 text-white rounded-lg hover:bg-honey-500 disabled:opacity-50 transition-all"
          >
            {loading ? (
              <RefreshCw className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Sparkles className="w-3.5 h-3.5" />
            )}
            {writeup ? "Regenerate" : "Generate"}
          </button>
        </div>
      </div>

      <p className="text-xs text-bark-400">
        A draft narrative summary of this property — pulls from rent roll,
        operating statement, location, and nearby business signals. Always
        review before sending to clients.
      </p>

      {writeup && (
        <div className="bg-grove-50 rounded-lg border border-grove-200 p-4">
          <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-bark-700">
            {writeup.markdown}
          </pre>
        </div>
      )}
    </div>
  );
}
